// CMS-Service für die Themen-Taxonomie — Admin-Endpunkte /api/admin/topic-categories.
// Auth-Muster identisch zu artistService (eigene axios-Instanz + Bearer aus der Supabase-Session).
//
// ⚠️ Bis 2026-08-11 lag die Taxonomie im `localStorage` DIESES Browsers, und niemand las sie:
// die Content-Formulare bezogen die Kategorien aus einem hartkodierten Enum. Was hier gepflegt
// wurde, kam nirgends an. Seit Scheibe A (PR #623) liegt sie in der Datenbank und ist die eine
// Quelle für CMS und premium-web. Plan: docs/plans/plan-themen-kategorien-2026-08.md
//
// ⚠️ Alle Methoden sind jetzt ASYNCHRON und können fehlschlagen — vorher waren es synchrone
// localStorage-Zugriffe. Aufrufer brauchen Lade- und Fehlerzustände.
import axios, { AxiosInstance } from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAccessToken } from '../lib/authToken';

export interface MainTopicCategoryData {
  id: string;
  /** Slug, wie er in `content.metadata.topicCategories` steht — serverseitig unveränderlich. */
  key: string;
  label: string;
  position: number;
  isActive: boolean;
}

export interface SubTopicCategoryData {
  id: string;
  key: string;
  label: string;
  categoryId: string;
  position: number;
  isActive: boolean;
}

/** Antwortform des Baums: Hauptthemen mit eingebetteten Unterthemen. */
interface TopicTreeNode extends MainTopicCategoryData {
  subcategories: SubTopicCategoryData[];
}

class TopicCategoryService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' },
    });
    this.api.interceptors.request.use(async (config) => {
      const token = await getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  /** Der volle Baum inklusive deaktivierter Themen (Verwaltungssicht). */
  private async getTree(): Promise<TopicTreeNode[]> {
    const res = await this.api.get<{ data: TopicTreeNode[] }>('/api/admin/topic-categories');
    return res.data.data ?? [];
  }

  /**
   * Beide Ebenen aus EINEM Abruf — Hauptthemen flach, Unterthemen flach daneben.
   *
   * ⚠️ Vorher gab es `getMainTopics()` und `getSubTopics()`, die JEWEILS `getTree()` riefen: die
   * einzige Oberfläche braucht beides und holte damit dieselbe Antwort zweimal übers Netz.
   * Bewusst gelöst durch EINEN Aufruf statt durch einen Cache in der Service-Schicht: die
   * Verwaltungsseite lädt nach jedem Anlegen/Ändern/Deaktivieren neu und erwartet dabei frische
   * Daten — ein Cache müsste an genau diesen Stellen wieder invalidiert werden. Ein blosses
   * In-Flight-Bündeln würde die Doppelung nur verstecken statt sie zu entfernen.
   */
  async getTopics(): Promise<{ mainTopics: MainTopicCategoryData[]; subTopics: SubTopicCategoryData[] }> {
    const tree = await this.getTree();
    return {
      // Bewusst Feld für Feld statt Rest-Destrukturierung: die Antwort trägt den ganzen Baum,
      // hier soll nur der Kopf durch.
      mainTopics: tree.map((node) => ({
        id: node.id,
        key: node.key,
        label: node.label,
        position: node.position,
        isActive: node.isActive,
      })),
      subTopics: tree.flatMap((main) => main.subcategories),
    };
  }

  async createMainTopic(data: { key: string; label: string; position?: number }): Promise<MainTopicCategoryData> {
    const res = await this.api.post<{ data: MainTopicCategoryData }>('/api/admin/topic-categories', data);
    return res.data.data;
  }

  async createSubTopic(data: {
    key: string;
    label: string;
    categoryId: string;
    position?: number;
  }): Promise<SubTopicCategoryData> {
    const res = await this.api.post<{ data: SubTopicCategoryData }>(
      '/api/admin/topic-categories/subcategories',
      data
    );
    return res.data.data;
  }

  /**
   * ⚠️ `key` fehlt in der Nutzlast bewusst und lässt sich auch nicht nachreichen: der Slug steht
   * in den Bestandsinhalten. Der Server verwirft ihn zusätzlich (TopicCategoryService).
   */
  async updateMainTopic(
    id: string,
    data: { label?: string; position?: number; isActive?: boolean }
  ): Promise<MainTopicCategoryData> {
    const res = await this.api.patch<{ data: MainTopicCategoryData }>(`/api/admin/topic-categories/${id}`, data);
    return res.data.data;
  }

  async updateSubTopic(
    id: string,
    data: { label?: string; position?: number; isActive?: boolean; categoryId?: string }
  ): Promise<SubTopicCategoryData> {
    const res = await this.api.patch<{ data: SubTopicCategoryData }>(
      `/api/admin/topic-categories/subcategories/${id}`,
      data
    );
    return res.data.data;
  }

  /**
   * Deaktiviert ein Hauptthema samt Unterthemen. Bewusst KEIN Löschen — die Slugs stehen ohne
   * Fremdschlüssel in `content.metadata.topicCategories`; ein Hard-Delete wäre für die Datenbank
   * folgenlos und für die Inhalte unsichtbar zerstörerisch.
   */
  async deactivateMainTopic(id: string): Promise<number> {
    const res = await this.api.post<{ data: { deactivatedSubcategories: number } }>(
      `/api/admin/topic-categories/${id}/deactivate`
    );
    return res.data.data?.deactivatedSubcategories ?? 0;
  }

  /** Einzelnes Unterthema abschalten — dafür genügt das Update. */
  async deactivateSubTopic(id: string): Promise<SubTopicCategoryData> {
    return this.updateSubTopic(id, { isActive: false });
  }
}

export const topicCategoryService = new TopicCategoryService();
